import * as React from 'react';
import { Maximize2, MessageSquare, Minimize2, ZoomIn, ZoomOut } from 'lucide-react';
import { WorkspaceButton } from '@/components/ui/workspace';
import { cn } from '@/lib/utils/cn';

/**
 * The full-screen process map (5 Nov 2026): the map takes the whole window
 * under its own toolbar; the processes rail and the app around it are
 * covered. The chat, a step's evidence and the versions list, which dock
 * beside the canvas otherwise, float over its bottom right corner as one
 * card and minimise to a Chat button, the way a support chat does.
 *
 * This amends OQ-V for this view only: a card may cover part of the
 * diagram, never the bpmn.io watermark (`MapCanvas` places the card in a
 * layer that ends above it). Compare with today draws two canvases, so
 * there the panel stays docked.
 */

/** The frame around the Process maps screen: the whole window when on. */
export const FULL_SCREEN_FRAME = 'fixed inset-0 z-30 flex flex-col bg-paper-0';

export function FullScreenToggle({
  on,
  onChange,
}: {
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  const Icon = on ? Minimize2 : Maximize2;
  const label = on ? 'Exit full screen' : 'Full screen';
  return (
    <WorkspaceButton
      aria-label={label}
      aria-pressed={on}
      title={on ? 'Exit full screen (Esc)' : 'Show the map on the full screen'}
      className="w-[30px] px-0"
      onClick={() => onChange(!on)}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </WorkspaceButton>
  );
}

/** Zoom out, fit and zoom in, as one group in the toolbar. */
export function ZoomButtons({
  onZoomIn,
  onZoomOut,
  onFit,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
}) {
  return (
    <div role="group" aria-label="Zoom" className="flex flex-none items-center">
      <WorkspaceButton
        aria-label="Zoom out"
        title="Zoom out"
        className="w-[30px] rounded-r-none px-0"
        onClick={onZoomOut}
      >
        <ZoomOut className="h-3.5 w-3.5" aria-hidden="true" />
      </WorkspaceButton>
      <WorkspaceButton
        title="Fit the whole map to the screen"
        className="-ml-px rounded-none"
        onClick={onFit}
      >
        Fit
      </WorkspaceButton>
      <WorkspaceButton
        aria-label="Zoom in"
        title="Zoom in"
        className="-ml-px w-[30px] rounded-l-none px-0"
        onClick={onZoomIn}
      >
        <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
      </WorkspaceButton>
    </div>
  );
}

/**
 * The card the chat, a step or the versions float in, over the corner of
 * the map. The chat keeps one height, so the thread does not jump as it
 * grows (`fill`); a step and the versions are as tall as what they hold.
 */
export function FloatingPanel({
  fill = false,
  children,
}: {
  fill?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid="map-floating-panel"
      className={cn(
        'flex max-h-full w-[400px] max-w-[calc(100vw-56px)] flex-col overflow-hidden rounded-card border border-ink-200 bg-paper-0 shadow-modal',
        fill && 'h-[600px]',
      )}
    >
      {children}
    </div>
  );
}

/** The chat, minimised: one button in the corner of the map. */
export function ChatLauncher({
  pending,
  onOpen,
}: {
  /** A change proposed by chat is shown on the map and waits for Apply or Discard. */
  pending: boolean;
  onOpen: () => void;
}) {
  return (
    <WorkspaceButton
      variant="primary"
      size="md"
      className="rounded-full px-4 shadow-modal"
      title="Change this map by chat"
      onClick={onOpen}
    >
      <MessageSquare className="h-4 w-4" aria-hidden="true" />
      {pending ? 'Chat · 1 change to apply' : 'Chat'}
    </WorkspaceButton>
  );
}

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
/** A menu, dialog or sheet opened from the map: it owns Escape and Tab while it is open. */
const LAYER = '[role="dialog"], [role="menu"], [role="listbox"]';

/**
 * Escape on the full-screen map. Left alone while typing, while a menu or
 * dialog is open (Escape closes that) and while the canvas is being edited
 * (Escape cancels a gesture there).
 */
export function useFullScreenEscape(on: boolean, onEscape: () => void): void {
  const latest = React.useRef(onEscape);
  latest.current = onEscape;
  React.useEffect(() => {
    if (!on) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest(EDITABLE)) return;
      if (document.querySelector(LAYER)) return;
      if (document.querySelector('[data-testid="map-canvas"][data-editable="true"]')) return;
      latest.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [on]);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Tab stays on the full-screen map: the app behind it is covered, so focus
 * must not walk into what cannot be seen.
 */
export function useFocusLoop(on: boolean, ref: React.RefObject<HTMLElement>): void {
  React.useEffect(() => {
    if (!on) return;
    const onKey = (e: KeyboardEvent) => {
      const root = ref.current;
      if (e.key !== 'Tab' || !root) return;
      const active = document.activeElement;
      if (active && !root.contains(active) && active.closest(LAYER)) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (!active || !root.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [on, ref]);
}
